import { createContext, useContext, ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getImpersonationData } from '@/utils/clientUtils';

export type UserRole = 'admin' | 'client' | 'setter' | 'closer';
export type ClientPackage = 'standard' | 'elite';

// Role-check helpers (Phase 16-07). Prefer these over inline `role === 'admin'`
// ternaries — they make the 4-role enum explicit and prevent implicit-binary
// regressions where a team role silently falls into the client branch.
export const isAdmin = (r: UserRole) => r === 'admin';
export const isClient = (r: UserRole) => r === 'client';
export const isTeam = (r: UserRole) => r === 'setter' || r === 'closer';

interface RoleContextType {
  role: UserRole;
  adminSubRole: string | null;
  clientPackage: ClientPackage;
  clientId: string | null;
  isImpersonating: boolean;
}

const RoleContext = createContext<RoleContextType | null>(null);

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within RoleProvider');
  }
  return context;
}

interface RoleProviderProps {
  children: ReactNode;
  session: Session | null;
}

export function RoleProvider({ children, session }: RoleProviderProps) {
  const metadata = session?.user?.user_metadata;

  // Check if admin is impersonating a client
  const impersonationData = getImpersonationData();
  const isImpersonating = !!impersonationData;

  const accountType = metadata?.account_type;
  const metadataRole = metadata?.role as string | undefined;
  const userId = session?.user?.id;

  // Team-role fallback re-query: if metadata says team but lacks role, look up admin_users.
  // Safest fallback (Pitfall 2): prevents malformed team sessions silently becoming 'client'
  // without at least consulting the source of truth.
  const { data: fallbackTeamRole } = useQuery({
    queryKey: ['admin_users_role_fallback', userId],
    enabled: !!userId && accountType === 'team' && !metadataRole,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('admin_users')
        .select('role')
        .eq('user_id', userId!)
        .eq('status', 'active')
        .maybeSingle();
      return (data as { role: string } | null)?.role ?? null;
    },
  });

  // Derive role across 4 possibilities.
  let role: UserRole;
  if (accountType === 'admin' && !isImpersonating) {
    role = 'admin';
  } else if (accountType === 'team') {
    const effectiveRole = metadataRole ?? fallbackTeamRole ?? null;
    if (effectiveRole === 'closer') role = 'closer';
    else if (effectiveRole === 'setter') role = 'setter';
    else role = 'client'; // narrowest-permissions fallback
  } else {
    role = 'client';
  }

  // Admin sub-role for permission checks (super_admin, team_admin, member)
  // Preserved even during impersonation so admin-only UI guards still work
  const adminSubRole = accountType === 'admin' ? (metadata?.role ?? 'member') : null;

  // Client ID: prefer session metadata (real client login), fall back to impersonation data
  const clientId: string | null =
    metadata?.client_id ??
    impersonationData?.clientId ??
    null;

  // Client package: prefer metadata, fallback to localStorage (set by impersonation or legacy)
  const clientPackage: ClientPackage =
    metadata?.client_package === 'elite' ? 'elite' :
    metadata?.client_package === 'standard' ? 'standard' :
    (localStorage.getItem('client_package') === 'elite' ? 'elite' : 'standard');

  return (
    <RoleContext.Provider value={{ role, adminSubRole, clientPackage, clientId, isImpersonating }}>
      {children}
    </RoleContext.Provider>
  );
}
